import { routePaths } from "@/App";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChatMessageWithSender } from "@/libs/api/types";
import { ChevronLeft, DeleteIcon, FileIcon, SendIcon } from "lucide-react";
import {
  type FormEvent,
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useChat } from "../hooks/useChat";
import { useChatEvents } from "../hooks/useChatEvents";
import { useChatUpload } from "../hooks/useChatUpload";
import { formatFileSize, getInitials, isSameDay } from "../utils";
import { ChatMessage, ChatMessageDateLabel } from "./ChatMessage";
import { FileInput } from "./FileInput";

interface ChatConversationProps {
  currentConversationId?: string;
}

function ChatConversationLayout({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col h-full">{children}</div>;
}

export function ChatConversation({
  currentConversationId,
}: ChatConversationProps) {
  const { t } = useTranslation();

  const {
    conversation,
    loading,
    error,
    hasMoreMessages,
    handleLoadMore,
    isLoadingMore,
  } = useChat(currentConversationId);

  const { sendMessage, markAsRead } = useChatEvents();

  if (loading) {
    return (
      <ChatConversationLayout>
        <ChatHeader
          name=""
          profileUrl={conversation?.participants?.[0]?.avatarUrl}
        />
        <div className="grid place-items-center h-full w-full">
          <LoadingSpinner />
        </div>
      </ChatConversationLayout>
    );
  }

  if (error) {
    return (
      <ChatConversationLayout>
        <ChatHeader name="" profileUrl={null} />
        <div className="grid place-items-center h-full w-full">
          <span className="text-destructive">
            {t("chat.errorLoadingChats")}
          </span>
        </div>
      </ChatConversationLayout>
    );
  }

  if (!conversation || !currentConversationId) {
    return (
      <ChatConversationLayout>
        <ChatHeader name="" profileUrl={null} />
        <div className="grid place-items-center h-full w-full">
          <div className="text-center space-y-2 max-w-[350px]">
            <h1 className="text-[20px] font-semibold">
              {t("chat.noConversationSelected.title")}
            </h1>
            <p className="text-gray-500">
              {t("chat.noConversationSelected.description")}
            </p>
          </div>
        </div>
      </ChatConversationLayout>
    );
  }

  const participant = conversation?.participants?.find(
    (participant) => participant.employeeId !== conversation.senderId,
  );

  return (
    <ChatConversationLayout>
      <ChatHeader
        name={participant?.name}
        profileUrl={conversation?.participants?.[0]?.avatarUrl}
      />

      <ChatMessageList
        messages={conversation.messages || []}
        hasMoreMessages={hasMoreMessages}
        isLoadingMore={isLoadingMore}
        senderId={conversation.senderId}
        currentConversationId={currentConversationId}
        handleLoadMore={handleLoadMore}
        onMarkAsRead={markAsRead}
      />

      <ChatFooter
        conversationId={currentConversationId}
        onMessageSend={sendMessage}
      />
    </ChatConversationLayout>
  );
}

interface ChatMessageListProps {
  messages: ChatMessageWithSender[];
  currentConversationId: string;
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
  senderId: string;
  handleLoadMore: () => void;
  onMarkAsRead: (messageId: string, conversationId: string) => void;
}

function ChatMessageList({
  messages,
  hasMoreMessages,
  isLoadingMore,
  currentConversationId,
  senderId,
  handleLoadMore,
  onMarkAsRead,
}: ChatMessageListProps) {
  const { t } = useTranslation();

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = listRef.current;
    if (!container) {
      return;
    }

    const lastElement = container.lastElementChild;
    if (!lastElement) {
      return;
    }

    const distanceFromBottom =
      container.scrollHeight - (container.scrollTop + container.clientHeight);

    const scrollOffset = 200;
    const isNearBottom = distanceFromBottom < scrollOffset;
    if (!isNearBottom) {
      return;
    }

    lastElement.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages]);

  useLayoutEffect(() => {
    const container = listRef.current;
    if (!container) {
      return;
    }

    container.scrollTo(0, container.scrollHeight);
  }, [currentConversationId]);

  if (messages.length === 0) {
    return (
      <div className="grid place-items-center h-full w-full">
        <span className="">{t("chat.noMessages")}</span>
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className="flex flex-col gap-4 overflow-y-auto flex-1 p-4 bg-list-background"
    >
      {hasMoreMessages && (
        <div className="flex justify-center pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            isLoading={isLoadingMore}
          >
            {t("chat.loadPreviousMessages")}
          </Button>
        </div>
      )}

      {messages.map((message, index) => {
        const sender = {
          id: message.senderId,
          name: message.senderName,
          avatarUrl: message.senderAvatarUrl,
        };

        const previousMessage = index > 0 ? messages[index - 1] : null;
        const showDateBadge = !isSameDay(
          previousMessage?.createdAt
            ? new Date(previousMessage.createdAt)
            : new Date(0),
          message.createdAt ? new Date(message.createdAt) : new Date(),
        );

        return (
          <Fragment key={message.id}>
            {showDateBadge && message.createdAt && (
              <ChatMessageDateLabel date={new Date(message.createdAt)} />
            )}

            <ChatMessage
              message={message}
              conversationId={currentConversationId}
              avatarUrl={sender.avatarUrl}
              senderName={sender.name}
              senderId={sender.id}
              currentUserId={senderId}
              onMarkAsRead={onMarkAsRead}
            />
          </Fragment>
        );
      })}
    </div>
  );
}

interface ChatFooterProps {
  conversationId: string;
  onMessageSend: (
    message: string,
    messageType: string,
    conversationId: string,
  ) => void;
}

function ChatFooter({ conversationId, onMessageSend }: ChatFooterProps) {
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { uploadFile, loading } = useChatUpload();

  const handleResetInput = () => {
    setMessage("");
    setSelectedFile(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (message.trim() === "" && !selectedFile) {
      return;
    }

    if (selectedFile) {
      await uploadFile(selectedFile, message);
    } else {
      onMessageSend(message, "text", conversationId);
    }
    handleResetInput();
  };

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  return (
    <div className="border-t">
      {selectedFile && (
        <div className="p-3  bg-gray-50 border-b">
          <div className="flex items-center gap-3 px-3 py-1 bg-quaternary-light border-quaternary border rounded-sm ">
            <span className="text-2xl">
              <FileIcon />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {selectedFile.name}
              </p>
              <p className="text-sm text-gray-500">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <Button size="icon" variant="ghost" onClick={handleRemoveFile}>
              <DeleteIcon />
            </Button>
          </div>
        </div>
      )}

      <form className="flex p-3" onSubmit={handleSubmit}>
        <div className="flex items-center gap-3 w-full">
          <FileInput
            selectedFile={selectedFile}
            onFileSelect={handleFileSelect}
          />
          <Input
            placeholder="Nachricht schreiben..."
            className="h-12"
            value={message}
            onChange={({ target }) => setMessage(target.value)}
          />
          <Button
            type="submit"
            className="h-12 w-24 rounded-md"
            variant="default"
            size="icon"
            isLoading={loading}
          >
            <SendIcon className="w-5 h-5" />
          </Button>
        </div>
      </form>
    </div>
  );
}

interface ChatHeaderProps {
  name: string | undefined;
  profileUrl?: string | null;
}

function ChatHeader({ name, profileUrl }: ChatHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="border-b p-4 flex-shrink-0">
      <Link
        to={routePaths.chat}
        className="flex items-center font-medium gap-1.5 bg-slate-100 p-2 pr-4 rounded w-fit mb-4"
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
        {t("common.back")}
      </Link>

      <div className="flex items-center gap-3">
        {profileUrl && (
          <Avatar className="h-10 w-10">
            <AvatarImage src={profileUrl} alt={name} />
            <AvatarFallback className="text-gray-400 text-sm font-medium">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
        )}

        <div className="flex flex-col">
          <h2 className="text-xl font-semibold">{name}</h2>
        </div>
      </div>
    </div>
  );
}
